/**
 * Volume Profile — Visible Range (TradingView-style)
 * Recalculates on every scroll/zoom based on visible candles only.
 * Shows POC, VAH, VAL with horizontal volume bars.
 */

const NUM_BINS = 80;
const VALUE_AREA_PCT = 0.70;

class VolumeProfilePV {
  constructor(s) { this._s = s; }
  update() {}
  renderer() {
    const s = this._s;
    return { draw: (t) => { t.useBitmapCoordinateSpace(({ context: ctx, bitmapSize: bs, horizontalPixelRatio: hpr, verticalPixelRatio: vpr }) => {
      // Recompute on every draw based on current visible range
      s._computeFromVisibleRange();

      const { bins, pocIndex, vahIndex, valIndex, maxVol } = s._computed;
      if (!bins || bins.length === 0 || !s._series) return;

      const width = bs.width / hpr;
      const barMaxWidth = width * 0.18;

      ctx.save();

      for (let i = 0; i < bins.length; i++) {
        const bin = bins[i];
        if (bin.volume === 0) continue;

        const yTop = s._series.priceToCoordinate(bin.high);
        const yBot = s._series.priceToCoordinate(bin.low);
        if (yTop == null || yBot == null) continue;

        const barH = Math.max(Math.abs(yBot - yTop) * vpr, 1);
        const barW = (bin.volume / maxVol) * barMaxWidth * hpr;
        const x = bs.width - barW;
        const y = Math.min(yTop, yBot) * vpr;

        if (i === pocIndex) {
          ctx.fillStyle = '#f59e0b';
          ctx.globalAlpha = 0.75;
        } else if (i >= valIndex && i <= vahIndex) {
          ctx.fillStyle = '#6366f1';
          ctx.globalAlpha = 0.3;
        } else {
          ctx.fillStyle = '#8b8fa3';
          ctx.globalAlpha = 0.15;
        }

        ctx.fillRect(x, y, barW, barH);
      }

      // POC line
      if (pocIndex >= 0 && pocIndex < bins.length) {
        const pocPrice = (bins[pocIndex].high + bins[pocIndex].low) / 2;
        const pocY = s._series.priceToCoordinate(pocPrice);
        if (pocY != null) {
          ctx.globalAlpha = 0.9;
          ctx.strokeStyle = '#f59e0b';
          ctx.lineWidth = 1 * hpr;
          ctx.setLineDash([4 * hpr, 3 * hpr]);
          ctx.beginPath();
          ctx.moveTo(0, pocY * vpr);
          ctx.lineTo(bs.width, pocY * vpr);
          ctx.stroke();
          ctx.setLineDash([]);

          ctx.fillStyle = '#f59e0b';
          ctx.font = `bold ${10 * hpr}px -apple-system, sans-serif`;
          ctx.fillText(`POC ${pocPrice.toFixed(2)}`, 4 * hpr, pocY * vpr - 3 * vpr);
        }
      }

      // VAH/VAL
      [{ idx: vahIndex, label: 'VAH' }, { idx: valIndex, label: 'VAL' }].forEach(({ idx, label }) => {
        if (idx >= 0 && idx < bins.length) {
          const price = label === 'VAH' ? bins[idx].high : bins[idx].low;
          const y = s._series.priceToCoordinate(price);
          if (y != null) {
            ctx.globalAlpha = 0.6;
            ctx.strokeStyle = '#818cf8';
            ctx.lineWidth = 0.8 * hpr;
            ctx.setLineDash([2 * hpr, 3 * hpr]);
            ctx.beginPath();
            ctx.moveTo(0, y * vpr);
            ctx.lineTo(bs.width, y * vpr);
            ctx.stroke();
            ctx.setLineDash([]);

            ctx.fillStyle = '#818cf8';
            ctx.font = `${9 * hpr}px -apple-system, sans-serif`;
            ctx.fillText(`${label} ${price.toFixed(2)}`, 4 * hpr, y * vpr - 2 * vpr);
          }
        }
      });

      ctx.restore();
    })}};
  }
}

export class VolumeProfilePrimitive {
  constructor() {
    this._chart = null;
    this._series = null;
    this._requestUpdate = null;
    this._view = new VolumeProfilePV(this);
    this._computed = { bins: [], pocIndex: -1, vahIndex: -1, valIndex: -1, maxVol: 0 };
    this._candles = [];
    this._lastRangeKey = '';
  }

  attached({ requestUpdate, chart, series }) {
    this._requestUpdate = requestUpdate;
    this._chart = chart;
    this._series = series;

    // Subscribe to visible range changes to trigger redraws
    this._onRangeChange = () => this._requestUpdate?.();
    chart.timeScale().subscribeVisibleLogicalRangeChange(this._onRangeChange);
  }

  detached() {
    if (this._chart && this._onRangeChange) {
      try { this._chart.timeScale().unsubscribeVisibleLogicalRangeChange(this._onRangeChange); } catch {}
    }
    this._requestUpdate = null;
    this._chart = null;
    this._series = null;
  }

  updateCandles(candles) {
    this._candles = candles;
    this._lastRangeKey = ''; // force recompute
    this._requestUpdate?.();
  }

  _computeFromVisibleRange() {
    if (!this._chart || !this._candles || this._candles.length < 5) return;

    const ts = this._chart.timeScale();
    const range = ts.getVisibleLogicalRange();
    if (!range) return;

    const from = Math.max(0, Math.floor(range.from));
    const to = Math.min(this._candles.length - 1, Math.ceil(range.to));
    const rangeKey = `${from}-${to}`;
    if (rangeKey === this._lastRangeKey) return; // no change
    this._lastRangeKey = rangeKey;

    const visibleCandles = this._candles.slice(from, to + 1);
    this._compute(visibleCandles);
  }

  _compute(candles) {
    if (!candles || candles.length < 5) {
      this._computed = { bins: [], pocIndex: -1, vahIndex: -1, valIndex: -1, maxVol: 0 };
      return;
    }

    let minPrice = Infinity, maxPrice = -Infinity;
    for (const c of candles) {
      if (c.low < minPrice) minPrice = c.low;
      if (c.high > maxPrice) maxPrice = c.high;
    }
    const range = maxPrice - minPrice;
    if (range <= 0) return;
    const binSize = range / NUM_BINS;

    const bins = [];
    for (let i = 0; i < NUM_BINS; i++) {
      bins.push({ low: minPrice + i * binSize, high: minPrice + (i + 1) * binSize, volume: 0 });
    }

    for (const c of candles) {
      const vol = c.volume || 1;
      for (let i = 0; i < NUM_BINS; i++) {
        const overlap = Math.min(c.high, bins[i].high) - Math.max(c.low, bins[i].low);
        if (overlap > 0) {
          const candleRange = c.high - c.low || binSize;
          bins[i].volume += vol * (overlap / candleRange);
        }
      }
    }

    let maxVol = 0, pocIndex = 0;
    for (let i = 0; i < bins.length; i++) {
      if (bins[i].volume > maxVol) { maxVol = bins[i].volume; pocIndex = i; }
    }

    const totalVol = bins.reduce((s, b) => s + b.volume, 0);
    const targetVol = totalVol * VALUE_AREA_PCT;
    let vaVol = bins[pocIndex].volume;
    let vaLow = pocIndex, vaHigh = pocIndex;
    while (vaVol < targetVol && (vaLow > 0 || vaHigh < bins.length - 1)) {
      const belowVol = vaLow > 0 ? bins[vaLow - 1].volume : 0;
      const aboveVol = vaHigh < bins.length - 1 ? bins[vaHigh + 1].volume : 0;
      if (belowVol >= aboveVol && vaLow > 0) { vaLow--; vaVol += bins[vaLow].volume; }
      else if (vaHigh < bins.length - 1) { vaHigh++; vaVol += bins[vaHigh].volume; }
      else break;
    }

    this._computed = { bins, pocIndex, vahIndex: vaHigh, valIndex: vaLow, maxVol };
  }

  updateAllViews() {}
  paneViews() { return [this._view]; }
}
